/**
 * The incomers' looks (see INCOMERS, night_plan.md): the things that come in
 * from outside the sphere while a sun is out. Unlike the other creatures they
 * aren't rigs: each is a painter in raw canvas, in world units (y up, a block
 * a unit), redrawn to a texture a few times a second (see NightMesh in
 * CreatureMesh.ts), and drawn the same way on the preview page.
 *
 * The shared look: dark iron plate and rivets, glass lenses lit cold teal
 * (the palette's colour for anything from somewhere else), and on the eel
 * pale smooth flesh held by iron. Lenses and arcs also go to a glow layer
 * when one is set (see setGlowLayer), which the game blooms. Stalled (`on`
 * false), the lenses are dead glass.
 */
type Ctx = CanvasRenderingContext2D;
type Rgb = [number, number, number];
type Pt = [number, number];
type Draw = (c: Ctx) => void;

/** What every painter's told: its clock (s), whether its lenses are lit, and how lit it is by the world (0..1). */
export interface NightPose {
  t: number;
  on: boolean;
  l: number;
}

// ------------------------------------------------------------------ helpers
const css = (c: Rgb, l = 1, a = 1): string => {
  const q = (v: number) => Math.round(Math.max(0, Math.min(1, v * l)) * 255);
  return `rgba(${q(c[0])},${q(c[1])},${q(c[2])},${a})`;
};
const mul = (c: Rgb, k: number): Rgb => [c[0] * k, c[1] * k, c[2] * k];
const pt = (pts: Pt[], i: number): Pt => pts[Math.max(0, Math.min(pts.length - 1, i))] ?? [0, 0];
function hash(id: string, salt = 0): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return ((h >>> 0) % 10007) / 10007;
}
function fill(ctx: Ctx, color: string | CanvasGradient, draw: Draw): void {
  ctx.beginPath();
  draw(ctx);
  ctx.fillStyle = color;
  ctx.fill();
}
function stroke(ctx: Ctx, color: string, width: number, draw: Draw): void {
  ctx.beginPath();
  draw(ctx);
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.stroke();
}
function dot(ctx: Ctx, x: number, y: number, r: number, color: string, ry = r, rot = 0): void {
  fill(ctx, color, (c) => c.ellipse(x, y, r, ry, rot, 0, Math.PI * 2));
}
function at(ctx: Ctx, x: number, y: number, rot: number, fn: Draw, sx = 1, sy = 1): void {
  ctx.save();
  ctx.translate(x, y);
  if (rot) ctx.rotate(rot);
  if (sx !== 1 || sy !== 1) ctx.scale(sx, sy);
  fn(ctx);
  ctx.restore();
}
const ease = (x: number) => x * x * (3 - 2 * x);

/** Where lenses and arcs are drawn a second time, to bloom (the game sets one; the preview doesn't). */
let glowLayer: Ctx | null = null;
export function setGlowLayer(ctx: Ctx | null): void {
  glowLayer = ctx;
}
/** Draw `draw` on the glow layer too, in the same place. */
function onGlow(ctx: Ctx, draw: (g: Ctx) => void): void {
  if (!glowLayer) return;
  glowLayer.save();
  glowLayer.setTransform(ctx.getTransform());
  draw(glowLayer);
  glowLayer.restore();
}
function glow(ctx: Ctx, color: Rgb, blur: number, draw: Draw): void {
  ctx.save();
  ctx.shadowColor = css(color);
  ctx.shadowBlur = blur;
  fill(ctx, css(color), draw);
  fill(ctx, css(color), draw);
  ctx.restore();
  onGlow(ctx, (g) => fill(g, css(color), draw));
}
function glowStroke(ctx: Ctx, color: Rgb, blur: number, width: number, draw: Draw): void {
  ctx.save();
  ctx.shadowColor = css(color);
  ctx.shadowBlur = blur;
  stroke(ctx, css(color), width, draw);
  stroke(ctx, css(color), width, draw);
  ctx.restore();
  onGlow(ctx, (g) => stroke(g, css(color), width, draw));
}

// ------------------------------------------------------------------ palette
const IRON: Record<"dark" | "mid" | "light" | "rivet", Rgb> = { dark: [0.15, 0.16, 0.19], mid: [0.3, 0.32, 0.36], light: [0.45, 0.47, 0.51], rivet: [0.58, 0.6, 0.63] };
const FLESH: Record<"pale" | "mid" | "dark", Rgb> = { pale: [0.82, 0.76, 0.74], mid: [0.64, 0.54, 0.56], dark: [0.44, 0.34, 0.38] };
/** Lens light, over 1 so it blooms; an arc is white-blue. */
const LENS: Rgb = [0.5, 1.8, 2.6];
const ARC: Rgb = [1.6, 2.2, 3.0];
const DEAD: Rgb = [0.12, 0.15, 0.17];
const NIGHT_ROCK: Rgb = [0.2, 0.19, 0.2];

// ------------------------------------------------------------------ parts
/** A slab of iron, lit top-left, rimmed dark. */
function plate(ctx: Ctx, l: number, x: number, y: number, w: number, h: number, r = 0.08, k = 1): void {
  const rr: Draw = (c) => c.roundRect(x, y, w, h, r);
  fill(ctx, css(IRON.mid, l * k), rr);
  stroke(ctx, css(IRON.dark, l * k), 0.06, rr);
  stroke(ctx, css(IRON.light, l * k), 0.04, (c) => {
    c.moveTo(x + r, y + h - 0.04);
    c.lineTo(x + w - r, y + h - 0.04);
    c.moveTo(x + 0.04, y + h - r);
    c.lineTo(x + 0.04, y + r);
  });
}
function rivets(ctx: Ctx, l: number, pts: Pt[], r = 0.045): void {
  for (const [x, y] of pts) {
    dot(ctx, x, y, r, css(IRON.dark, l));
    dot(ctx, x - r * 0.25, y + r * 0.25, r * 0.65, css(IRON.rivet, l));
  }
}
/** A lens: an iron ring round a glass eye, lit when `on`, dead glass otherwise. */
function lens(ctx: Ctx, l: number, x: number, y: number, r: number, on: boolean): void {
  dot(ctx, x, y, r + 0.07, css(IRON.dark, l));
  dot(ctx, x, y, r + 0.03, css(IRON.light, l));
  if (on) glow(ctx, LENS, 6 + r * 30, (c) => c.arc(x, y, r, 0, Math.PI * 2));
  else {
    dot(ctx, x, y, r, css(DEAD, l));
    dot(ctx, x - r * 0.35, y + r * 0.35, r * 0.28, css([0.3, 0.34, 0.37], l));
  }
}
/** A leg of two tubes, hip to knee to foot; `far` legs are the far side, dimmer. */
function leg(ctx: Ctx, l: number, hx: number, hy: number, kx: number, ky: number, fx: number, fy: number, w: number, far = false): void {
  const k = far ? 0.65 : 1;
  const p: Draw = (c) => {
    c.moveTo(hx, hy);
    c.lineTo(kx, ky);
    c.lineTo(fx, fy);
  };
  stroke(ctx, css(IRON.dark, l * k), w + 0.07, p);
  stroke(ctx, css(IRON.mid, l * k), w, p);
  dot(ctx, kx, ky, w * 0.8, css(IRON.light, l * k));
  dot(ctx, hx, hy, w * 0.7, css(IRON.dark, l * k));
}
/** A clamp: two prongs, open by `open` (0..1). */
function clamp(ctx: Ctx, l: number, x: number, y: number, dir: number, open: number, size = 0.35): void {
  at(ctx, x, y, dir, (c) => {
    plate(c, l, -0.12, -0.14, 0.24, 0.28, 0.05);
    for (const s of [-1, 1]) {
      const a = s * (0.25 + open * 0.6);
      const prong: Draw = (p) => {
        p.moveTo(0.1, 0);
        p.lineTo(0.1 + Math.cos(a) * size, Math.sin(a) * size);
        p.lineTo(0.1 + Math.cos(a) * size + 0.12, Math.sin(a) * size * 0.4);
      };
      stroke(c, css(IRON.dark, l), 0.14, prong);
      stroke(c, css(IRON.light, l), 0.07, prong);
    }
  });
}
/** A jagged arc of light between two points. */
function arc(ctx: Ctx, x0: number, y0: number, x1: number, y1: number, t: number): void {
  const n = 7;
  const f = Math.floor(t * 18);
  glowStroke(ctx, ARC, 10, 0.05, (c) => {
    c.moveTo(x0, y0);
    for (let i = 1; i < n; i++) {
      const u = i / n;
      const j = (hash("arc", f * 11 + i) - 0.5) * 0.4;
      c.lineTo(x0 + (x1 - x0) * u - (y1 - y0) * j, y0 + (y1 - y0) * u + (x1 - x0) * j);
    }
    c.lineTo(x1, y1);
  });
}
/** A chunk of rock, as a warden tears up and throws. */
function chunk(ctx: Ctx, l: number, x: number, y: number, r: number, rot: number): void {
  at(ctx, x, y, rot, (c) => {
    fill(c, css(mul(NIGHT_ROCK, 1.4), l), (p) => {
      p.moveTo(-r, -r * 0.5);
      p.lineTo(-r * 0.4, -r);
      p.lineTo(r * 0.8, -r * 0.7);
      p.lineTo(r, r * 0.3);
      p.lineTo(r * 0.2, r);
      p.lineTo(-r * 0.8, r * 0.6);
      p.closePath();
    });
    fill(c, css(mul(NIGHT_ROCK, 0.9), l), (p) => {
      p.moveTo(-r * 0.4, -r);
      p.lineTo(r * 0.8, -r * 0.7);
      p.lineTo(r, r * 0.3);
      p.lineTo(r * 0.1, 0);
      p.closePath();
    });
  });
}
/** The column of teal light a warden stands in: faint, wide, a seam at its foot. */
function column(ctx: Ctx, x: number, w: number, h: number, t: number): void {
  const g = ctx.createLinearGradient(0, 0, 0, h);
  g.addColorStop(0, `rgba(80,220,255,${0.16 + Math.sin(t * 2) * 0.03})`);
  g.addColorStop(1, "rgba(80,220,255,0)");
  fill(ctx, g, (c) => c.rect(x - w / 2, 0, w, h));
  glow(ctx, LENS, 14, (c) => c.rect(x - w * 0.35, 0, w * 0.7, 0.06));
}

// ------------------------------------------------------------------ spider (harvestman)
/** How a spider stands: `gait` 0..1 how fast it's walking, `bite` 0..1 how far into a bite. Origin at its feet, facing +x. */
export interface SpiderPose extends NightPose {
  gait: number;
  bite: number;
}
/** The spider's reach (blocks, from its feet), for the texture it's drawn to. */
export const SPIDER_BOX = { x0: -4.4, x1: 4.4, y0: -0.3, y1: 4.2 };

/**
 * A spider: a body hardly there on eight legs out of all proportion, knees
 * far above it, the one lens hanging underneath in a cage; it dips and
 * snaps a pair of hooked fangs under the cage to bite.
 */
export function drawSpider(ctx: Ctx, p: SpiderPose): void {
  const { t, on, l, gait, bite } = p;
  const lunge = Math.sin(Math.min(1, bite) * Math.PI);
  const h = 2.3 + (on ? Math.sin(t * 2) * 0.06 : 0) - lunge * 0.5;
  const pace = 3.5 + gait * 2.5;
  at(ctx, lunge * 0.3, h, lunge * -0.12, (c) => {
    for (let i = 0; i < 4; i++) {
      for (const far of [true, false]) {
        const k = i - 1.5;
        const ph = Math.sin(t * pace + i * 1.6 + (far ? Math.PI : 0));
        const fx = k * 1.9 + gait * ph * 0.5 + (far ? -0.2 : 0.2);
        const fy = -h + gait * Math.max(0, ph) * 0.4;
        // Thick enough to read at play zoom: the legs are the silhouette.
        leg(c, l, k * 0.08, 0, k * 1.0 + (far ? -0.1 : 0.1), 1.3, fx, fy, 0.09, far);
      }
    }
    plate(c, l, -0.3, -0.16, 0.6, 0.32, 0.16);
    rivets(c, l, [[-0.15, 0], [0.15, 0]], 0.03);
    // The lens hangs under it in a little cage, swinging as it walks; the fangs below it snap to bite.
    const sw = gait * Math.sin(t * pace - 0.5) * 0.15;
    stroke(c, css(IRON.dark, l), 0.04, (q) => {
      q.moveTo(0, -0.16);
      q.lineTo(sw, -0.45);
    });
    at(c, sw, -0.68, sw, (q) => {
      lens(q, l, 0, 0, 0.17, on);
      stroke(q, css(IRON.mid, l), 0.035, (r) => {
        for (const a of [-1, -0.33, 0.33, 1]) {
          r.moveTo(Math.sin(a) * 0.26, 0.24);
          r.lineTo(Math.sin(a) * 0.2, -0.25);
        }
        r.moveTo(-0.26, -0.25);
        r.lineTo(0.26, -0.25);
      });
      const gape = 0.12 + lunge * 0.3;
      for (const s of [-1, 1]) {
        const fang: Draw = (r) => {
          r.moveTo(s * 0.1, -0.27);
          r.quadraticCurveTo(s * (0.18 + gape), -0.45, s * (0.06 + gape * 0.3), -0.62);
        };
        stroke(q, css(IRON.dark, l), 0.07, fang);
        stroke(q, css(IRON.light, l), 0.03, fang);
      }
    });
  });
}

/** A spider folded up to be carried: legs drawn up round its body like a shut umbrella, the lens under it. Origin at its hook. */
function foldedSpider(ctx: Ctx, l: number, on: boolean, s = 1): void {
  at(ctx, 0, 0, 0, (c) => {
    for (let i = 0; i < 4; i++) {
      for (const side of [-1, 1]) {
        const x = side * (0.12 + i * 0.05);
        stroke(c, css(IRON.dark, l * (side < 0 ? 0.65 : 1)), 0.05, (q) => {
          q.moveTo(x * 0.5, -0.35);
          q.lineTo(x * 2.2, -0.05);
          q.lineTo(x * 1.2, -1.2 - i * 0.08);
        });
      }
    }
    plate(c, l, -0.22, -0.5, 0.44, 0.26, 0.12);
    lens(c, l, 0, -0.72, 0.11, on);
  }, s, s);
}

// ------------------------------------------------------------------ warden (frame)
/** How a warden stands: `gait` 0..1 walking, `wind` 0..1 through winding up a throw (it holds a rock), `follow` 0..1 the arm swinging through after. Origin at its feet. */
export interface WardenPose extends NightPose {
  gait: number;
  wind: number;
  follow: number;
}
export const WARDEN_BOX = { x0: -2.6, x1: 2.9, y0: -0.2, y1: 6.2 };

/**
 * A warden: a tall iron frame on two long legs, a three-by-three grid of
 * lenses in the chest, a blind block for a head, clamps for hands, standing
 * in a column of teal light. Winds up and throws overarm.
 */
export function drawWarden(ctx: Ctx, p: WardenPose): void {
  const { t, on, l, gait, wind, follow } = p;
  if (on) column(ctx, 0, 2.4, 6, t);
  const sway = on ? Math.sin(t * 1.3) * 0.04 : 0;
  at(ctx, 0, 0, sway, (c) => {
    for (let i = 0; i < 2; i++) {
      const s = i ? 1 : -1;
      const step = gait * Math.sin(t * 2.6 + i * Math.PI);
      const shift = on ? Math.sin(t * 1.3 + i * Math.PI) * 0.06 : 0;
      const lift = Math.max(0, step) * 0.25;
      leg(c, l, s * 0.45, 2.2, s * 0.55 + 0.35 + step * 0.2, 1.2 + shift + lift, s * 0.8 + step * 0.35, lift * 0.6, 0.17, i === 0);
      plate(c, l * (i ? 1 : 0.65), s * 0.8 + step * 0.35 - 0.3, lift * 0.6 - 0.02, 0.6, 0.16, 0.04);
    }
    plate(c, l, -0.7, 2.0, 1.4, 1.9, 0.1);
    plate(c, l, -0.55, 2.15, 1.1, 1.6, 0.06);
    for (let r = 0; r < 3; r++) for (let q = 0; q < 3; q++) lens(c, l, -0.33 + q * 0.33, 2.45 + r * 0.5, 0.1, on);
    plate(c, l, -0.9, 3.8, 1.8, 0.25, 0.06);
    plate(c, l, -0.3, 4.05, 0.6, 0.45, 0.08);
    rivets(c, l, [[-0.8, 3.92], [0.8, 3.92], [-0.6, 2.1], [0.6, 2.1]], 0.05);
    for (let i = 0; i < 2; i++) {
      const s = i ? 1 : -1;
      // The near arm winds up over its head, then swings through.
      const raise = i === 1 ? ease(Math.min(1, wind)) * 2.4 - ease(Math.min(1, follow)) * 2.9 * (wind > 0 ? 0 : 1) : 0;
      const a = -Math.PI / 2 + Math.max(-0.5, raise) + (on ? Math.sin(t * 1.3 + i) * 0.05 : 0);
      const ex = s * 0.9 + Math.cos(a) * 1.2;
      const ey = 3.7 + Math.sin(a) * 1.2;
      const kx = s * 0.9 + Math.cos(a - 0.4 * s) * 0.7;
      const ky = 3.7 + Math.sin(a - 0.4 * s) * 0.7;
      leg(c, l, s * 0.9, 3.7, kx, ky, ex, ey, 0.13, i === 0);
      clamp(c, l * (i ? 1 : 0.65), ex, ey, a, i === 1 && wind <= 0 && follow > 0 ? 0.9 : 0.2, 0.4);
      if (i === 1 && wind > 0) chunk(c, l, ex + Math.cos(a) * 0.5, ey + Math.sin(a) * 0.5, 0.3, a);
    }
  });
}

// ------------------------------------------------------------------ carrier (gantry)
/** How a carrier hangs: `lowering` 0..1 through letting its spiders down on their lines, `held` how many spiders are hung up in it. Origin at its middle. */
export interface CarrierPose extends NightPose {
  lowering: number;
  held: number;
  /** Where its searchlight points (radians off straight down). */
  sweep: number;
}
export const CARRIER_BOX = { x0: -2.7, x1: 2.7, y0: -6.2, y1: 2.8 };

/**
 * A carrier: an open iron frame under a flat rotor, its spiders folded on
 * hooks inside in plain view, one big searchlight lens underneath sweeping
 * the rock. Lowering, the hooks pay their spiders out on lines below.
 */
export function drawCarrier(ctx: Ctx, p: CarrierPose): void {
  const { t, on, l, lowering, held, sweep } = p;
  const bar = (c: Ctx, x0: number, y0: number, x1: number, y1: number, w = 0.1) => {
    const line: Draw = (q) => {
      q.moveTo(x0, y0);
      q.lineTo(x1, y1);
    };
    stroke(c, css(IRON.dark, l), w + 0.05, line);
    stroke(c, css(IRON.mid, l), w, line);
  };
  // The searchlight's cone, under everything.
  if (on) {
    at(ctx, 0, -0.5, sweep, (c) => {
      const g = c.createLinearGradient(0, 0, 0, -5.6);
      g.addColorStop(0, "rgba(80,220,255,0.22)");
      g.addColorStop(1, "rgba(80,220,255,0)");
      fill(c, g, (q) => {
        q.moveTo(-0.2, 0);
        q.lineTo(-1.5, -5.6);
        q.lineTo(1.5, -5.6);
        q.lineTo(0.2, 0);
        q.closePath();
      });
    });
  }
  bar(ctx, -1.9, 0, 1.9, 0, 0.12);
  bar(ctx, -1.9, 1.6, 1.9, 1.6, 0.12);
  for (const x of [-1.9, -0.65, 0.65, 1.9]) bar(ctx, x, 0, x, 1.6);
  bar(ctx, -1.9, 0, -0.65, 1.6, 0.05);
  bar(ctx, 0.65, 1.6, 1.9, 0, 0.05);
  rivets(ctx, l, [[-1.9, 0], [-0.65, 0], [0.65, 0], [1.9, 0], [-1.9, 1.6], [-0.65, 1.6], [0.65, 1.6], [1.9, 1.6]], 0.05);
  // Its spiders, folded on hooks; lowering, they go down on their lines out of the bottom of the frame.
  [-1.3, 1.3].forEach((x, i) => {
    if (i >= held) return;
    const y = 1.1 - ease(Math.min(1, lowering)) * 3.2;
    stroke(ctx, css(IRON.light, l), 0.03, (q) => {
      q.moveTo(x, 1.55);
      q.lineTo(x, y);
    });
    at(ctx, x, y, Math.sin(t * 1.5 + x) * 0.08, (q) => foldedSpider(q, l, on, 0.85));
  });
  // The rotor: a flat disc on a mast, blades sweeping round it.
  bar(ctx, 0, 1.6, 0, 2.3, 0.14);
  at(ctx, 0, 2.3, 0, (c) => {
    dot(c, 0, 0, 2.3, css(IRON.dark, l), 0.14);
    const spin = on ? t * 3 : 0.8;
    for (let i = 0; i < 4; i++) {
      const a = spin + (i / 4) * Math.PI * 2;
      const sx = Math.cos(a);
      stroke(c, css(IRON.light, l * (0.6 + 0.4 * Math.max(0, Math.sin(a)))), 0.08, (q) => {
        q.moveTo(0, 0);
        q.lineTo(sx * 2.2, 0.08 + Math.abs(sx) * 0.04);
      });
    }
    dot(c, 0, 0, 0.2, css(IRON.light, l), 0.1);
  });
  // The searchlight lens on its gimbal.
  bar(ctx, 0, 0, 0, -0.35, 0.08);
  at(ctx, 0, -0.5, sweep, (c) => {
    plate(c, l, -0.35, -0.2, 0.7, 0.5, 0.1);
    lens(c, l, 0, -0.1, 0.22, on);
  });
}

// ------------------------------------------------------------------ eel (chain)
/** How an eel swims: `latched` onto a hull (its arc crackling), `speed` 0..1 how hard it's swimming. Origin at its head, facing +x; its body trails back along -x. */
export interface EelPose extends NightPose {
  latched: boolean;
  speed: number;
}
export const EEL_BOX = { x0: -11.2, x1: 1.6, y0: -2.2, y1: 2.2 };

/**
 * An eel: iron lantern-cages strung on a wire, something pale alive in each,
 * a bigger cage with pincers for a head and the arc between them. Swims
 * stiffly link by link; latched, it hangs off its pincers and twitches.
 */
export function drawEel(ctx: Ctx, p: EelPose): void {
  const { t, on, l, latched, speed } = p;
  const lit = l * 0.8 + 0.3;
  const n = 32;
  const len = 10;
  const amp = latched ? 0.35 : 0.6 + speed * 0.5;
  const wave = latched ? 9 : 2.6 + speed * 2;
  const pts: Pt[] = [];
  for (let i = 0; i <= n; i++) {
    const u = i / n;
    // Still at the head, swinging most at the tail.
    pts.push([-len + u * len, Math.sin(u * 4.2 - t * wave) * amp * (1 - u) * 1.4 + (latched ? Math.sin(t * 23 + u * 9) * 0.05 * (1 - u) : 0)]);
  }
  const line: Draw = (c) => pts.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
  if (on) glowStroke(ctx, [0.4, 1.2, 1.6], 4, 0.025, line);
  else stroke(ctx, css(IRON.light, lit), 0.025, line);
  const links = 8;
  for (let k = 0; k < links; k++) {
    const i = Math.round((k / links) * (n - 3)) + 1;
    const [x, y] = pt(pts, i);
    const [nx, ny] = pt(pts, i + 1);
    const a = Math.atan2(ny - y, nx - x) + Math.sin(t * 3 + k) * 0.1;
    const s = 0.55 + (k / links) * 0.25;
    at(ctx, x, y, a, (c) => {
      dot(c, 0, 0, s * 0.42, css(FLESH.pale, lit), s * 0.3);
      dot(c, s * 0.1, -s * 0.05, s * 0.18, css(FLESH.mid, lit), s * 0.12);
      if (on) dot(c, s * 0.05, s * 0.05, 0.03, css([0.9, 0.5, 0.6], lit));
      stroke(c, css(IRON.dark, lit), 0.07, (q) => q.roundRect(-s * 0.5, -s * 0.35, s, s * 0.7, 0.12));
      stroke(c, css(IRON.dark, lit), 0.045, (q) => {
        for (const u of [-0.25, 0, 0.25]) {
          q.moveTo(u * s, -s * 0.35);
          q.lineTo(u * s, s * 0.35);
        }
      });
      stroke(c, css(IRON.light, lit), 0.025, (q) => {
        q.moveTo(-s * 0.5, s * 0.35);
        q.lineTo(s * 0.5, s * 0.35);
      });
      rivets(c, lit, [[-s * 0.5, 0], [s * 0.5, 0]], 0.035);
    });
  }
  // The head cage: bigger, pincers out the front, a lens on top.
  const [hx, hy] = pt(pts, n);
  const [px, py] = pt(pts, n - 1);
  at(ctx, hx - 0.2, hy, Math.atan2(hy - py, hx - px), (c) => {
    dot(c, -0.15, 0, 0.42, css(FLESH.pale, lit), 0.32);
    dot(c, -0.05, 0.05, 0.14, css(FLESH.dark, lit), 0.1);
    stroke(c, css(IRON.dark, lit), 0.08, (q) => q.roundRect(-0.6, -0.38, 0.95, 0.76, 0.14));
    stroke(c, css(IRON.dark, lit), 0.05, (q) => {
      for (const u of [-0.35, -0.1, 0.15]) {
        q.moveTo(u, -0.38);
        q.lineTo(u, 0.38);
      }
    });
    lens(c, lit, -0.1, 0.5, 0.1, on);
    const gape = latched ? 0.15 : 0.4 + Math.sin(t * 5) * 0.1;
    for (const s of [-1, 1]) {
      const pincer: Draw = (q) => {
        q.moveTo(0.3, s * 0.2);
        q.quadraticCurveTo(0.8, s * (gape + 0.3), 1.0, s * gape);
      };
      stroke(c, css(IRON.dark, lit), 0.1, pincer);
      stroke(c, css(IRON.light, lit), 0.04, pincer);
    }
    if (on) arc(c, 1.0, gape, 1.0, -gape, t);
    // Latched, the arc burns into the plate it holds.
    if (on && latched) glow(c, [1.6, 0.9, 0.4], 12, (q) => q.arc(1.25, 0, 0.08 + Math.sin(t * 9) * 0.03, 0, Math.PI * 2));
  });
}

// ------------------------------------------------------------------ the preview's scenery
/** Dark rock across the card to stand on, its top at `y`, a lit edge along it. Night: no grass colour to speak of. */
export function nightGround(ctx: Ctx, l: number, y = 0): void {
  at(ctx, 0, y, 0, (c) => {
    fill(c, css(NIGHT_ROCK, l), (q) => q.rect(-14, -14, 28, 14));
    fill(c, css(mul(NIGHT_ROCK, 1.5), l), (q) => q.rect(-14, -0.1, 28, 0.1));
    for (let x = -13; x < 14; x += 1.7) dot(c, x + hash("pebble", Math.round(x * 10)) * 0.8, -0.3 - hash("pd", Math.round(x * 10)) * 0.6, 0.18, css(mul(NIGHT_ROCK, 0.7), l), 0.1);
  });
}

/** An ironclad hull across the card from `y` up, for an eel to latch onto. */
export function nightHull(ctx: Ctx, l: number, y: number): void {
  fill(ctx, css(IRON.dark, l), (c) => c.rect(-14, y, 28, 14));
  for (let x = -13.6; x < 14; x += 1.6) plate(ctx, l, x, y + 0.05, 1.5, 0.9, 0.06);
  rivets(ctx, l, Array.from({ length: 17 }, (_, i): Pt => [-13.4 + i * 1.6, y + 0.8]), 0.05);
}

/** A rock thrown by a warden, `u` (0..1) of the way along its flight out to the right from (x0, y0). */
export function thrownRock(ctx: Ctx, l: number, x0: number, y0: number, u: number): void {
  chunk(ctx, l, x0 + u * 9, y0 + u * 2 - u * u * 4, 0.3, u * 6);
}
